import React from 'react';

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '';
  let num = value;
  if (typeof value === 'string') {
    num = parseFloat(value.replace(/\./g, '').replace(',', '.'));
  }
  if (isNaN(num)) return value;
  const fixed = num.toFixed(2);
  const [intPart, decPart] = fixed.split('.');
  const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${withThousands},${decPart}`;
};

const parseSpanishNumber = (value) => {
  if (!value || value === '') return 0;
  if (typeof value === 'number') return value;
  const cleaned = value.replace(/\./g, '').replace(',', '.');
  return parseFloat(cleaned) || 0;
};

function LineItemsTable({ items, totales, onItemsChange, onTotalesChange }) {
  const handleItemChange = (index, field, value, isNumber = false) => {
    const newItems = [...items];
    const parsedValue = isNumber ? parseSpanishNumber(value) : value;
    newItems[index] = { ...newItems[index], [field]: parsedValue };
    onItemsChange(newItems);
  };

  const handleTotalChange = (field, value) => {
    onTotalesChange({ ...totales, [field]: parseSpanishNumber(value) });
  };

  const renderInput = (value, onChange, isNumber = false) => (
    <input
      type="text"
      className={`table-input ${isNumber ? 'number' : ''}`}
      value={isNumber ? formatNumber(value) : (value ?? '')}
      onChange={(e) => onChange(e.target.value)}
      placeholder="-"
    />
  );

  const renderTotalInput = (value, onChange) => (
    <input
      type="text"
      className="total-input"
      value={formatNumber(value)}
      onChange={(e) => onChange(e.target.value)}
      placeholder="-"
    />
  );

  return (
    <div className="line-items-container">
      <div className="table-header">
        <h3>Lineas del documento</h3>
      </div>

      {items.length === 0 ? (
        <div className="table-empty">
          <p>No se encontraron lineas de detalle</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="items-table">
            <thead>
              <tr>
                <th>Descripcion</th>
                <th className="number">Cantidad</th>
                <th className="number">Precio</th>
                <th className="number">Dto.</th>
                <th className="number">IVA %</th>
                <th className="number">Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={index}>
                  <td>{renderInput(item.descripcion, (v) => handleItemChange(index, 'descripcion', v, false))}</td>
                  <td className="number">{renderInput(item.cantidad, (v) => handleItemChange(index, 'cantidad', v, true), true)}</td>
                  <td className="number">{renderInput(item.precio_unitario, (v) => handleItemChange(index, 'precio_unitario', v, true), true)}</td>
                  <td className="number">{renderInput(item.descuento, (v) => handleItemChange(index, 'descuento', v, true), true)}</td>
                  <td className="number">{renderInput(item.iva, (v) => handleItemChange(index, 'iva', v, true), true)}</td>
                  <td className="number">{renderInput(item.total_linea, (v) => handleItemChange(index, 'total_linea', v, true), true)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="totales-section">
        <div className="total-row">
          <span>Base imponible:</span>
          <span className="total-value">{renderTotalInput(totales.base_imponible, (v) => handleTotalChange('base_imponible', v))} EUR</span>
        </div>
        <div className="total-row">
          <span>Total IVA:</span>
          <span className="total-value">{renderTotalInput(totales.total_iva, (v) => handleTotalChange('total_iva', v))} EUR</span>
        </div>
        <div className="total-row final">
          <span>TOTAL:</span>
          <span className="total-value">{renderTotalInput(totales.total, (v) => handleTotalChange('total', v))} EUR</span>
        </div>
      </div>

      <style>{`
        .line-items-container {
          background: var(--card-background);
          border-radius: var(--border-radius);
          box-shadow: var(--shadow);
          overflow: hidden;
        }

        .table-header {
          padding: 12px 16px;
          background: #374151;
          color: white;
        }

        .table-header h3 {
          font-size: 14px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .table-empty {
          padding: 32px;
          text-align: center;
          color: var(--text-secondary);
        }

        .table-wrapper {
          overflow-x: auto;
        }

        .items-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
        }

        .items-table th,
        .items-table td {
          padding: 8px 10px;
          text-align: left;
          border-bottom: 1px solid var(--border-color);
          vertical-align: top;
        }

        .items-table th:first-child,
        .items-table td:first-child {
          width: 40%;
        }

        .items-table th:not(:first-child),
        .items-table td:not(:first-child) {
          width: 12%;
        }

        .items-table th {
          background: #f9fafb;
          font-size: 11px;
          font-weight: 600;
          color: var(--text-secondary);
          text-transform: uppercase;
        }

        .items-table td {
          font-size: 13px;
        }

        .items-table .number {
          text-align: right;
        }

        .items-table td:first-child .table-input {
          white-space: normal;
          word-wrap: break-word;
        }

        .items-table tr:hover {
          background: #f9fafb;
        }

        .totales-section {
          padding: 16px;
          background: #f9fafb;
          border-top: 2px solid var(--border-color);
        }

        .total-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          font-size: 14px;
        }

        .total-row.final {
          font-weight: 600;
          font-size: 16px;
          padding-top: 12px;
          margin-top: 8px;
          border-top: 1px solid var(--border-color);
        }

        .total-value {
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .table-input {
          width: 100%;
          padding: 6px 8px;
          font-size: 14px;
          border: 1px solid transparent;
          border-radius: 4px;
          background: transparent;
          transition: border-color 0.2s, background 0.2s;
        }

        .table-input:hover {
          background: #f3f4f6;
        }

        .table-input:focus {
          outline: none;
          border-color: var(--primary-color);
          background: #fff;
        }

        .table-input.number {
          text-align: right;
        }

        .table-input::placeholder {
          color: var(--text-secondary);
        }

        .total-input {
          width: 80px;
          padding: 4px 8px;
          font-size: inherit;
          font-weight: inherit;
          border: 1px solid transparent;
          border-radius: 4px;
          background: transparent;
          text-align: right;
          transition: border-color 0.2s, background 0.2s;
        }

        .total-input:hover {
          background: #e5e7eb;
        }

        .total-input:focus {
          outline: none;
          border-color: var(--primary-color);
          background: #fff;
        }

        .total-input::placeholder {
          color: var(--text-secondary);
        }
      `}</style>
    </div>
  );
}

export default LineItemsTable;
